export const PERIOD_TOTAL_LABEL = "Total for period";

const round3 = (value) => Math.round(value * 1000) / 1000;

const computeRpm = (impressions, revenue) => (impressions > 0 ? round3(revenue / (impressions / 1000)) : 0);

const makeRow = (website, date, impressions, revenue) => ({
    date,
    website,
    impressions,
    "publisher revenue": revenue,
    rpm: computeRpm(impressions, revenue),
});

/**
 * Builds CSV rows for a multi-website yield report from per-website daily records
 * (the same data the stacked charts use).
 *
 * Output layout, in order:
 *   - for every date: one row per website
 *   - a period summary at the end: one row per website over the whole period, then "Total for period"
 *
 * Period rows carry "<from> - <to>" in the date column.
 *
 * @param {Array<{website: string, date: string, impressions: number, "publisher revenue": number}>} records
 * @returns {Array<{website: string, date: string, impressions: number, "publisher revenue": number, rpm: number}>}
 */
export const buildMultiWebsiteCsvRows = (records) => {
    // Rows not approved for statistics are left out of the report and its totals
    records = records ? records.filter((item) => item["is_approved"] !== false) : records;
    if (!records || records.length === 0) {
        return [];
    }

    const dates = [...new Set(records.map((item) => item.date))].sort((a, b) => new Date(a) - new Date(b));
    const websites = [...new Set(records.map((item) => item.website))].sort();

    const rows = [];
    const periodByWebsite = {};
    websites.forEach((website) => {
        periodByWebsite[website] = { impressions: 0, revenue: 0 };
    });

    dates.forEach((date) => {
        websites.forEach((website) => {
            const dayRecords = records.filter((item) => item.date === date && item.website === website);
            // A website may have several rows per day (e.g. split by slot), sum them up
            const impressions = dayRecords.reduce((sum, item) => sum + (item.impressions || 0), 0);
            const revenue = dayRecords.reduce((sum, item) => sum + (item["publisher revenue"] || 0), 0);
            if (dayRecords.length === 0) {
                return;
            }
            rows.push(makeRow(website, date, impressions, revenue));
            periodByWebsite[website].impressions += impressions;
            periodByWebsite[website].revenue += revenue;
        });
    });

    const period = `${dates[0]} - ${dates[dates.length - 1]}`;
    let periodImpressions = 0;
    let periodRevenue = 0;
    websites.forEach((website) => {
        const { impressions, revenue } = periodByWebsite[website];
        rows.push(makeRow(website, period, impressions, revenue));
        periodImpressions += impressions;
        periodRevenue += revenue;
    });
    rows.push(makeRow(PERIOD_TOTAL_LABEL, period, periodImpressions, periodRevenue));

    return rows;
};
